// @ts-check
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box } from '~/design-system';

/**
 * Card surfaced below the upload dropzone explaining what the file should contain.
 * Compose with `RequirementRow`, `FileFormatRow`, and `RequiredFieldsList`.
 * @param {{ children: React.ReactNode }} props
 */
export default function FileRequirements({ children }) {
  return (
    <Box
      sx={{
        marginTop: 5,
        marginX: 'auto',
        width: '623px',
        maxWidth: '100%',
        textAlign: 'left',
        backgroundColor: 'brand.silver',
        border: '1px solid',
        borderColor: 'border',
        borderRadius: 'largest',
        padding: 3,
      }}
    >
      <Box
        sx={{
          fontSize: 'smallest',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'lighterText',
          marginBottom: 2,
        }}
      >
        <Trans>What your file needs</Trans>
      </Box>
      {children}
    </Box>
  );
}

/** @param {{ label: React.ReactNode, body: React.ReactNode, isLast?: boolean }} props */
export function RequirementRow({ label, body, isLast = false }) {
  return (
    <Box sx={{ marginBottom: isLast ? 0 : 2 }}>
      <Box
        sx={{
          fontSize: 'small',
          fontWeight: 600,
          color: 'text',
          marginBottom: '2px',
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontSize: 'small',
          color: 'lightText',
          lineHeight: 1.5,
        }}
      >
        {body}
      </Box>
    </Box>
  );
}

/** Standard ".xlsx, headers on any row" row used by every import type. */
export function FileFormatRow() {
  return (
    <RequirementRow
      label={<Trans>File format</Trans>}
      body={
        <Trans>
          A single <strong>.xlsx</strong> sheet. Headers can be on any row —
          you’ll point them out in the next step.
        </Trans>
      }
    />
  );
}

/**
 * Renders required columns from a `fieldsToImport`-shaped array.
 * Labels are MessageDescriptors (from `msg\`...\``) resolved at render time.
 * @param {{ fields: Array<{ key: string, label: import('@lingui/core').MessageDescriptor, required?: boolean }> }} props
 */
export function RequiredFieldsList({ fields }) {
  const { i18n } = useLingui();
  const required = fields.filter((f) => f.required);
  return (
    <Box as="ul" sx={{ margin: 0, paddingLeft: 18 }}>
      {required.map((field) => (
        <li key={field.key}>
          <strong>{i18n._(field.label)}</strong>
        </li>
      ))}
    </Box>
  );
}
